import React, {useContext, useState} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import Portal from '../../../shared/library/modals/base/portal';
import BaseModal from '../../../shared/library/modals/base/modal';
import {
  ASH,
  CONCRETE,
  FOCUS_BLUE,
  GUNSMOKE,
  PUMPKIN,
  TARMAC,
  WHITE
} from '../../../shared/style/colors';
import {BASE_TEXT, WEIGHT} from '../../../shared/style/typography';
import {ALPHA} from '../../../shared/style/color-utils';
import useStripe from '../../../shared/utils/hooks/stripe';
import PaymentSuccessIcon from '../../../shared/library/icons/payment-success.svg';
import {useSendAnalyticsEvent} from '../../../shared/enhancers/analytics-enhancer';
import {MobileContext} from '../../../shared/enhancers/mobile-enhancer';
import MobileModal from '../../../shared/library/modals/base/modal-mobile';
import MobilePortal from '../../../shared/library/modals/base/portal-mobile';
import {PHONE} from '../../../shared/style/breakpoints';
import {CurrentUserContext} from '../../../shared/enhancers/current-user-enhancer';

const Container = glamorous.div({
  display: 'flex',
  background: WHITE,
  flexDirection: 'column',
  padding: 50,
  alignItems: 'center'
});

const Message = glamorous.p({
  display: 'flex',
  alignItems: 'center',
  ...BASE_TEXT,
  fontSize: 29,
  marginBottom: 50,
  textTransform: 'uppercase',
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: 1,
  '>svg': {
    width: 50,
    height: 50,
    marginRight: 16
  }
});

const Plans = glamorous.div(
  {
    ...BASE_TEXT,
    fontSize: 18,
    display: 'grid',
    gridTemplateColumns: '1fr 20px 1fr',
    gridTemplateAreas: `
    "premName  . promName"
    "premDesc  . promDesc"
    "premPrice . promPrice"
    "premTerm  . promTerm"
    "premTrial . promTrial"
    "premBtn   . promBtn"
    "premList  . promList"
  `,
    width: '100%',
    '>*': {
      background: ALPHA(TARMAC, 0.08),
      opacity: 0.3
    },
    [PHONE]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows:
        'auto auto auto auto auto auto auto 20px auto auto auto auto auto auto auto',
      gridTemplateAreas: `
      "premName"
      "premDesc"
      "premPrice"
      "premTerm"
      "premTrial"
      "premBtn"
      "premList"
      "."
      "promName"
      "promDesc"
      "promPrice"
      "promTerm"
      "promTrial"
      "promBtn"
      "promList"
    `
    }
  },
  ({currentPlan}) =>
    currentPlan
      ? {
          [`>[data-plan=${currentPlan}]`]: {
            opacity: 1
          }
        }
      : {'>*': {opacity: 1}}
);

const Plan = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: WEIGHT.ULTRA_BLACK,
  fontSize: 29,
  color: '#454545',
  textTransform: 'uppercase',
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
  paddingTop: 40,
  position: 'relative'
});

const CurrentPlan = glamorous.div({
  background: '#ffbb0b',
  height: 32,
  width: '80%',
  paddingLeft: 30,
  paddingRight: 30,
  color: WHITE,
  fontSize: 11,
  textTransform: 'uppercase',
  position: 'absolute',
  top: -16,
  borderRadius: 2,
  boxShadow: '0 2px 4px 0 rgba(182, 182, 182, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  lineHeight: 1
});

const Description = glamorous.div({
  fontSize: 15,
  lineHeight: 25 / 15,
  color: '#8a8a8a',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  paddingLeft: 30,
  paddingRight: 30
});

const Trial = glamorous.div({
  ...BASE_TEXT,
  fontSize: 12,
  color: TARMAC,
  textAlign: 'center',
  fontStyle: 'italic',
  paddingLeft: 30,
  paddingRight: 30,
  paddingTop: 10
});

const Price = glamorous.div({
  color: PUMPKIN,
  fontWeight: WEIGHT.ULTRA_BLACK,
  fontSize: 50,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  lineHeight: 1,
  paddingTop: 20,
  '>span': {
    fontSize: 14,
    color: GUNSMOKE,
    marginLeft: 5
  }
});

const Term = glamorous.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  alignItems: 'center',
  paddingTop: 10,
  paddingLeft: 30,
  paddingRight: 30,
  '>button': {
    color: CONCRETE,
    fontWeight: WEIGHT.BOLD,
    fontSize: 11,
    textTransform: 'uppercase',
    background: 'transparent',
    outline: 'none',
    border: `1px solid ${ASH}`,
    '&:nth-child(1)': {
      borderTopLeftRadius: 2,
      borderBottomLeftRadius: 2,
      borderRight: 0
    },
    '&:nth-child(2)': {
      borderTopRightRadius: 2,
      borderBottomRightRadius: 2,
      borderLeft: 0
    },
    '&[data-active=true]': {
      background: PUMPKIN,
      borderColor: PUMPKIN,
      color: WHITE
    }
  }
});

const ButtonWrapper = glamorous.div({
  paddingLeft: 30,
  paddingRight: 30,
  paddingTop: 20
});

const Checkout = glamorous.button({
  width: '100%',
  height: 40,
  ...BASE_TEXT,
  fontSize: 13,
  borderRadius: 2,
  color: WHITE,
  fontWeight: WEIGHT.BOLD,
  outline: 'none',
  background: FOCUS_BLUE,
  border: 0
});

const Features = glamorous.div({
  paddingTop: 30,
  paddingBottom: 30,
  paddingRight: 15,
  borderBottomLeftRadius: 4,
  borderBottomRightRadius: 4,
  display: 'flex',
  justifyContent: 'center',
  color: '#8a8a8a',
  '>ul>li': {
    fontSize: 13,
    marginBottom: 14,
    lineHeight: 1.54,
    listStyleImage: `url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMSIgaGVpZ2h0PSIxMSIgdmlld0JveD0iMCAwIDExIDExIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGNpcmNsZSBjeD0iNS41IiBjeT0iNS41IiByPSI1LjUiIGZpbGw9IiMwNjhERkUiLz4KICAgICAgICA8cGF0aCBmaWxsPSIjRkZGIiBmaWxsLXJ1bGU9Im5vbnplcm8iIGQ9Ik01LjA2OSA3LjEwNWwtMi4xMTItMS44NS0uNzU3LjcyNCAyLjg2OSAyLjU3Nkw5LjUgNC4wMjUgOC43NDMgMy4zeiIvPgogICAgPC9nPgo8L3N2Zz4K)`
  }
});

const checkout = (plan, company, user, sendAnalyticsEvent) => {
  sendAnalyticsEvent('click_upgradePlan', {plan, companyId: company.id});

  // eslint-disable-next-line no-undef
  const stripe = Stripe(process.env.STRIPE_CLIENT_KEY);
  const url = window.location.href;
  stripe
    .redirectToCheckout({
      items: [{plan, quantity: 1}],
      successUrl: `${url}/success`,
      cancelUrl: url.substr(0, url.length - '/plan'.length),
      clientReferenceId: `Company_${company.id}`,
      customerEmail: user.email
    })
    .then(function(result) {
      if (result.error) {
        alert('There was a problem with checkout:' + result.error.message);
      }
    });
};

const contactSupport = () => {
  window.open(`mailto:david@stackshare.io?subject=Company+Profile+Plan+Support`, '_blank');
};

const PLAN_PREM = 'prem';
const PLAN_PROM = 'prom';
const TERM_YEARLY = 'y';
const TERM_MONTHLY = 'm';

const VALID_PLANS = [
  PLAN_PREM + TERM_MONTHLY,
  PLAN_PREM + TERM_YEARLY,
  PLAN_PROM + TERM_MONTHLY,
  PLAN_PROM + TERM_YEARLY
];

export const getCurrentPlan = plans => {
  if (plans && plans.length > 0) {
    const plan = plans.find(p => VALID_PLANS.includes(p.slug));
    if (plan) {
      return plan.slug;
    }
  }
  return null;
};

const getPlanTerm = plan => {
  if (plan && plan.endsWith(TERM_YEARLY)) {
    return TERM_YEARLY;
  } else if (plan && plan.endsWith(TERM_MONTHLY)) {
    return TERM_MONTHLY;
  }
  return null;
};

const getBasePlan = plan => {
  if (plan && plan.startsWith(PLAN_PREM)) {
    return PLAN_PREM;
  } else if (plan && plan.startsWith(PLAN_PROM)) {
    return PLAN_PROM;
  }
  return null;
};

const PlanModal = ({onDismiss, success, company}) => {
  const sendAnalyticsEvent = useSendAnalyticsEvent();
  const isMobile = useContext(MobileContext);
  const currentUser = useContext(CurrentUserContext);

  const currentPlan = getCurrentPlan(company.plans);

  const [promTerm, setPromTerm] = useState(
    getBasePlan(currentPlan) === PLAN_PROM ? getPlanTerm(currentPlan) : TERM_YEARLY
  );
  const [premTerm, setPremTerm] = useState(
    getBasePlan(currentPlan) === PLAN_PREM ? getPlanTerm(currentPlan) : TERM_YEARLY
  );

  useStripe();

  if (!process.env.STRIPE_CLIENT_KEY) {
    // eslint-disable-next-line no-console
    console.warn('STRIPE_CLIENT_KEY not configured!');
    return null;
  }

  const container = (
    <Container>
      {success && currentPlan && (
        <Message>
          <PaymentSuccessIcon /> successfully subscribed
        </Message>
      )}
      <Plans currentPlan={getBasePlan(currentPlan)}>
        <Plan style={{gridArea: 'premName'}} data-plan={PLAN_PREM}>
          premium
          {getBasePlan(currentPlan) === PLAN_PREM && <CurrentPlan>current plan</CurrentPlan>}
        </Plan>
        <Description style={{gridArea: 'premDesc'}} data-plan={PLAN_PREM}>
          Build your company’s engineering brand
        </Description>
        <Price style={{gridArea: 'premPrice'}} data-plan={PLAN_PREM}>
          ${premTerm === TERM_YEARLY ? '375' : '500'} <span>/mo</span>
        </Price>
        <Term style={{gridArea: 'premTerm'}} data-plan={PLAN_PREM}>
          <button
            disabled={currentPlan}
            onClick={() => setPremTerm(TERM_MONTHLY)}
            data-active={premTerm === TERM_MONTHLY}
          >
            billed monthly
          </button>
          <button
            disabled={currentPlan}
            onClick={() => setPremTerm(TERM_YEARLY)}
            data-active={premTerm === TERM_YEARLY}
          >
            billed annually
          </button>
        </Term>
        <Trial style={{gridArea: 'premTrial'}} data-plan={PLAN_PREM}>
          Yearly commitment required (optional 3-month paid trial)
        </Trial>
        <ButtonWrapper style={{gridArea: 'premBtn'}} data-plan={PLAN_PREM}>
          <Checkout
            onClick={() =>
              currentPlan
                ? contactSupport()
                : checkout(PLAN_PREM + premTerm, company, currentUser, sendAnalyticsEvent)
            }
          >
            {currentPlan ? 'Contact support' : 'Upgrade now'}
          </Checkout>
        </ButtonWrapper>
        <Features style={{gridArea: 'premList'}} data-plan={PLAN_PREM}>
          <ul>
            <li>Concierge Onboarding</li>
            <li>Unlimited Job Postings via Lever, Greenhouse, or Workday</li>
            <li>StackShare Job Search</li>
            <li>Unlock new profile sections</li>
            <li>Show Job Postings on your company&apos;s stack profiles</li>
            <li>Customized Banner Image</li>
          </ul>
        </Features>

        <Plan style={{gridArea: 'promName'}} data-plan={PLAN_PROM}>
          promoted
          {getBasePlan(currentPlan) === PLAN_PROM && <CurrentPlan>current plan</CurrentPlan>}
        </Plan>
        <Description style={{gridArea: 'promDesc'}} data-plan={PLAN_PROM}>
          Drive awareness for your eng job postings
        </Description>
        <Price style={{gridArea: 'promPrice'}} data-plan={PLAN_PROM}>
          ${promTerm === TERM_YEARLY ? '3,000' : '4,000'} <span>/mo</span>
        </Price>
        <Term style={{gridArea: 'promTerm'}} data-plan={PLAN_PROM}>
          <button
            disabled={currentPlan}
            onClick={() => setPromTerm(TERM_MONTHLY)}
            data-active={promTerm === TERM_MONTHLY}
          >
            billed monthly
          </button>
          <button
            disabled={currentPlan}
            onClick={() => setPromTerm(TERM_YEARLY)}
            data-active={promTerm === TERM_YEARLY}
          >
            billed annually
          </button>
        </Term>
        <Trial style={{gridArea: 'promTrial'}} data-plan={PLAN_PROM}>
          Yearly commitment required (optional 3-month paid trial)
        </Trial>
        <ButtonWrapper style={{gridArea: 'promBtn'}} data-plan={PLAN_PROM}>
          <Checkout
            onClick={() =>
              currentPlan
                ? contactSupport()
                : checkout(PLAN_PROM + promTerm, company, currentUser, sendAnalyticsEvent)
            }
          >
            {currentPlan ? 'Contact support' : 'Upgrade now'}
          </Checkout>
        </ButtonWrapper>
        <Features style={{gridArea: 'promList'}} data-plan={PLAN_PROM}>
          <ul>
            <li>Concierge Onboarding</li>
            <li>Unlimited Job Postings via Lever, Greenhouse, or Workday</li>
            <li>StackShare Job Search</li>
            <li>Unlock new profile sections</li>
            <li>Show Job Postings on your company&apos;s stack profiles</li>
            <li>Customized Banner Image</li>
            <li>Job Postings appear on Tool Profiles and Comparison Pages</li>
            <li>Feature job postings in the Weekly Newsletter</li>
            <li>Job Postings cycled into promoted space on Homepage</li>
            <li>Promote your Tech Stack</li>
            <li>One Featured Engineering Blog Post</li>
            <li>One Stack History Timeline</li>
          </ul>
        </Features>
      </Plans>
    </Container>
  );

  if (isMobile) {
    return (
      <MobilePortal>
        <MobileModal title="Company Profile Plans" onDismiss={onDismiss}>
          {container}
        </MobileModal>
      </MobilePortal>
    );
  } else {
    return (
      <Portal position="top" onDismiss={onDismiss} animateIn={false}>
        <BaseModal hideTitleBar={false} width={900} layout="none" title="Company Profile Plans">
          {container}
        </BaseModal>
      </Portal>
    );
  }
};

PlanModal.propTypes = {
  onDismiss: PropTypes.func,
  success: PropTypes.bool,
  company: PropTypes.object
};

export default PlanModal;
